<template>
  <div class="behaviour-manage-layout py-4">
    <CContainer>
      <CRow v-if="showStudentDetailView" class="align-items-center mb-3">
        <CCol cols="12" md="8">
          <h2 id="behaviour-manage-title" class="h4 mb-0 text-start">
            <button
              ref="studentDetailBackRef"
              type="button"
              class="btn btn-link p-0 text-decoration-none d-inline-flex align-items-center behaviour-manage-student-detail-heading"
              data-testid="behaviour-manage-student-detail-back"
              :aria-label="$t('behaviour_manage.back_to_student_list')"
              @click="clearSelectedStudent"
            >
              <i
                class="ri-arrow-left-s-line behaviour-manage-student-detail-heading__icon"
                aria-hidden="true"
              />
              <span class="ms-1 behaviour-manage-student-detail-heading__name">
                {{ studentDetailDisplayName }}
              </span>
            </button>
          </h2>
        </CCol>
      </CRow>
      <div
        v-if="showManageTabs"
        class="behaviour-manage-tabs d-flex align-items-stretch justify-content-start flex-wrap mb-3"
        role="tablist"
        :aria-label="behaviourTerminology.manageTabsAriaLabel"
        @keydown="onTabListKeydown"
      >
        <DashboardTabListItem
          v-for="tab in tabs"
          :key="tab.value"
          :label="tab.label"
          :value="tab.value"
          :tab-element-id="'behaviour-manage-tab-' + tab.value"
          :tab-panel-id="tab.panelId"
          :is-active="activeTab === tab.value"
          :show-check-icon="false"
          @on-tab-click="setActiveTab"
        />
      </div>
      <!-- Without the tablist there is no tab to label the panel, so the
           `tabpanel` role is dropped rather than left unnamed. -->
      <div
        v-if="isPointHistoryTabActive || isStudent"
        :role="showManageTabs ? 'tabpanel' : undefined"
        id="behaviour-manage-panel-point-history"
        :aria-labelledby="
          showManageTabs ? 'behaviour-manage-tab-' + activeTab : undefined
        "
      >
        <router-view v-slot="{ Component }">
          <component
            :is="Component"
            :key="pointHistoryScope"
            :scope="pointHistoryScope"
          />
        </router-view>
      </div>
      <div
        v-if="!isStudent && activeTab === TAB_STUDENT_LIST"
        :role="showManageTabs ? 'tabpanel' : undefined"
        id="behaviour-manage-panel-student-list"
        :aria-labelledby="
          showManageTabs ? 'behaviour-manage-tab-student-list' : undefined
        "
      >
        <BehaviourStudentListTab
          :selected-student="selectedStudent"
          @select-student="onStudentSelected"
          @clear-student="clearSelectedStudent"
        />
      </div>
      <div
        v-if="!isStudent && activeTab === TAB_UPLOAD_LOG"
        :role="showManageTabs ? 'tabpanel' : undefined"
        id="behaviour-manage-panel-upload-log"
        :aria-labelledby="
          showManageTabs ? 'behaviour-manage-tab-upload-log' : undefined
        "
      >
        <BehaviourUploadLogTab />
      </div>
    </CContainer>
  </div>
</template>

<script>
import behaviourConstants from "@/constants/behaviourManagementConstants.js"
import DashboardTabListItem from "@/v3components/Datatables/DashboardDataTable/TabsAndFilter/DashboardTabListItem.vue"
import BehaviourStudentListTab from "@/v3views/BehaviourStudentListTab.vue"
import BehaviourUploadLogTab from "@/v3views/BehaviourUploadLogTab.vue"
import { computed, nextTick, onMounted, ref, watch } from "vue"
import { useI18n } from "vue-i18n"
import { useRoute, useRouter } from "vue-router"
import { useStore } from "vuex"

const TAB_MY_POINT_HISTORY = "my-point-history"
const TAB_SCHOOL_WIDE_POINT_HISTORY = "school-wide-point-history"
const TAB_STUDENT_LIST = "student-list"
const TAB_UPLOAD_LOG = "upload-log"
const POINT_HISTORY_TABS = [TAB_MY_POINT_HISTORY, TAB_SCHOOL_WIDE_POINT_HISTORY]
const APP_TITLE_SUFFIX = "Securly Pass"

export default {
  name: "BehaviourManageLayout",
  components: {
    DashboardTabListItem,
    BehaviourStudentListTab,
    BehaviourUploadLogTab
  },
  setup() {
    const store = useStore()
    const { t } = useI18n()
    const route = useRoute()
    const router = useRouter()

    const activeTab = ref(TAB_MY_POINT_HISTORY)
    const selectedStudent = ref(null)
    const studentDetailBackRef = ref(null)
    let studentListTriggerId = null

    const behaviourTerminology = computed(
      () => store.getters["behaviourSettings/behaviourTerminology"]
    )

    const userRole = computed(() => store.getters["authentication/getUserRole"])

    const isStudent = computed(() => userRole.value === "student")

    const isAdmin = computed(() =>
      behaviourConstants.BEHAVIOUR_ADMIN_ROLES.includes(userRole.value)
    )

    const tabs = computed(() => {
      const bt = behaviourTerminology.value

      if (isStudent.value) {
        return [
          {
            label: bt.myPointHistoryTab,
            value: TAB_MY_POINT_HISTORY,
            panelId: "behaviour-manage-panel-point-history"
          }
        ]
      }

      const all = [
        {
          label: bt.myPointHistoryTab,
          value: TAB_MY_POINT_HISTORY,
          panelId: "behaviour-manage-panel-point-history"
        }
      ]

      if (isAdmin.value) {
        all.push({
          label: bt.schoolWidePointHistoryTab,
          value: TAB_SCHOOL_WIDE_POINT_HISTORY,
          panelId: "behaviour-manage-panel-point-history"
        })
      }

      all.push({
        label: bt.studentListTab,
        value: TAB_STUDENT_LIST,
        panelId: "behaviour-manage-panel-student-list"
      })

      all.push({
        label: bt.uploadLogTab,
        value: TAB_UPLOAD_LOG,
        panelId: "behaviour-manage-panel-upload-log"
      })

      return all
    })

    const isPointHistoryTabActive = computed(() =>
      POINT_HISTORY_TABS.includes(activeTab.value)
    )

    const pointHistoryScope = computed(() =>
      activeTab.value === TAB_SCHOOL_WIDE_POINT_HISTORY ? "schoolWide" : "mine"
    )

    const showStudentDetailView = computed(() => Boolean(selectedStudent.value))

    const studentDetailDisplayName = computed(() => {
      const student = selectedStudent.value
      const name = String(student?.studentName ?? "").trim()
      if (name) return name
      const num = String(student?.studentNumber ?? "").trim()
      if (num) return num
      return t("behaviour_manage.col_student")
    })

    const showManageTabs = computed(
      () => tabs.value.length > 1 && !showStudentDetailView.value
    )

    const focusActiveTab = () => {
      if (typeof document === "undefined") return
      document
        .getElementById(`behaviour-manage-tab-${activeTab.value}`)
        ?.focus?.()
    }

    const onTabListKeydown = (event) => {
      if (!["ArrowLeft", "ArrowRight", "Home", "End"].includes(event.key)) {
        return
      }

      const availableTabs = tabs.value
      if (!availableTabs.length) return

      event.preventDefault()
      const currentIndex = Math.max(
        0,
        availableTabs.findIndex((tab) => tab.value === activeTab.value)
      )
      let nextIndex = currentIndex

      if (event.key === "Home") nextIndex = 0
      else if (event.key === "End") nextIndex = availableTabs.length - 1
      else {
        const offset = event.key === "ArrowRight" ? 1 : -1
        nextIndex =
          (currentIndex + offset + availableTabs.length) % availableTabs.length
      }

      activeTab.value = availableTabs[nextIndex]?.value || activeTab.value
      nextTick(focusActiveTab)
    }

    const onStudentSelected = (student) => {
      if (!student?.studentId && !student?.id) return
      studentListTriggerId = student?.studentId || student?.id
      selectedStudent.value = { ...student }
      activeTab.value = TAB_STUDENT_LIST
      nextTick(() => studentDetailBackRef.value?.focus?.())
    }

    const clearSelectedStudent = async () => {
      selectedStudent.value = null
      await nextTick()

      const escapedId =
        typeof CSS !== "undefined" && CSS.escape
          ? CSS.escape(String(studentListTriggerId ?? ""))
          : String(studentListTriggerId ?? "").replace(/["\\]/g, "\\$&")
      const restoreFocus = () => {
        const trigger =
          typeof document !== "undefined" && escapedId
            ? document.querySelector(
                `[data-testid="behaviour-student-list-name-${escapedId}"]`
              )
            : null

        if (trigger) trigger.focus?.()
        else focusActiveTab()
        studentListTriggerId = null
      }

      if (typeof window !== "undefined" && window.requestAnimationFrame) {
        window.requestAnimationFrame(restoreFocus)
      } else {
        restoreFocus()
      }
    }

    const setActiveTab = (value) => {
      const tab = tabs.value.find((x) => x.value === value)
      if (!tab) return

      activeTab.value = value
      if (route.query?.tab !== value) {
        router.replace({
          query: {
            ...route.query,
            tab: value
          }
        })
      }
    }

    const applyTabFromRoute = () => {
      const tabFromQuery = String(route.query?.tab ?? "").trim()
      if (!tabFromQuery) return
      if (tabs.value.some((tab) => tab.value === tabFromQuery)) {
        activeTab.value = tabFromQuery
      }
    }

    const applyDocumentTitle = () => {
      if (typeof document === "undefined") return

      const baseTitle =
        behaviourTerminology.value?.behaviourManagement?.trim?.() || ""
      if (!baseTitle) return

      const alreadyHasSuffix = baseTitle
        .toLowerCase()
        .includes(APP_TITLE_SUFFIX.toLowerCase())
      document.title = alreadyHasSuffix
        ? baseTitle
        : `${baseTitle} - ${APP_TITLE_SUFFIX}`
    }

    watch(
      tabs,
      () => {
        if (
          tabs.value.length &&
          !tabs.value.some((tab) => tab.value === activeTab.value)
        ) {
          activeTab.value = tabs.value[0]?.value || TAB_MY_POINT_HISTORY
        }
        applyTabFromRoute()
      },
      { immediate: true }
    )

    watch(
      () => route.query?.tab,
      () => {
        applyTabFromRoute()
      }
    )

    watch(behaviourTerminology, applyDocumentTitle, { immediate: true })

    onMounted(() => {
      store.dispatch("behaviourSettings/fetchBehaviourTerminology")
      if (!isStudent.value) {
        store.dispatch("passBlocks/getGradeYears")
      }
      applyTabFromRoute()
    })

    return {
      TAB_STUDENT_LIST,
      TAB_UPLOAD_LOG,
      isStudent,
      tabs,
      isPointHistoryTabActive,
      pointHistoryScope,
      showManageTabs,
      showStudentDetailView,
      studentDetailDisplayName,
      selectedStudent,
      studentDetailBackRef,
      onStudentSelected,
      clearSelectedStudent,
      activeTab,
      setActiveTab,
      onTabListKeydown,
      behaviourTerminology
    }
  }
}
</script>
